/**
 * How one filter option reads: its mark, its name, and how many rows it holds.
 *
 * Every control that lists options — the inline chip menus and the
 * more-filters panel — lays a row out the same way, so a reader comparing
 * counts runs their eye down one column and a long name never pushes the
 * number off the end of it.
 *
 * The name takes the room that is left and truncates with an ellipsis rather
 * than wrapping: a wrapped row makes the list ragged and the counts stop
 * lining up, which is the one thing the column is for. The full text stays
 * available on hover, and on a phone — where hover does not exist — the panel
 * is wide enough that truncation is rare and a tap still selects the whole
 * option, so nothing is unreachable.
 *
 * Contains:
 * - OptionLabel — the option's name, truncated to one line, titled in full
 * - OptionCount — the count, right-aligned in its own column
 * - OPTION_LIST_MAX_WIDTH — how wide a list of options may grow
 * - NoOptions — what an option list says when it has none
 */

import { ListItemText, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * How wide an option list may grow before its labels truncate instead.
 *
 * Wide enough for the names a reader actually meets — "Temperature/Humidity
 * Alarm Sensor 2" fits — and narrow enough that a popover stays a popover
 * rather than a second page.
 */
export const OPTION_LIST_MAX_WIDTH = 340;

/**
 * The option's name, taking the room left beside its count.
 *
 * @param {{label: (string|React.ReactNode)}} props
 */
export function OptionLabel({ label }) {
  return (
    <ListItemText
      primary={label}
      // A string can be titled; a page's JSX label cannot, and is left alone.
      slotProps={{
        primary: {
          variant: 'body2',
          noWrap: true,
          title: typeof label === 'string' ? label : undefined,
        },
      }}
      sx={{ minWidth: 0, my: 0 }}
    />
  );
}

/**
 * How many rows the option holds, in its own column at the end of the row.
 *
 * Absent when the option names no count — which is not zero. Zero is a fact
 * about the list and is shown; a count nobody sent is not.
 *
 * @param {{count: (number|undefined)}} props
 */
export function OptionCount({ count }) {
  if (count === undefined || count === null) return null;
  return (
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      sx={{
        ml: 1.5,
        flexShrink: 0,
        // Figures of the same width, so the column reads as a column.
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count}
    </Typography>
  );
}

/**
 * What an option list says when it has none.
 *
 * A control whose options are the rows behind them — alarm categories, load
 * types — empties out when there is nothing to filter by, and an empty
 * popover reads as broken rather than as good news. It says which control
 * it is, so "No Alarms to show" answers the question the reader opened it
 * with. Shared by the FilterBar mobile sheet and the desktop quick-filter
 * popovers so the empty state reads the same everywhere.
 *
 * @param {{label: (string|React.ReactNode)}} props - the control's own label.
 */
export function NoOptions({ label }) {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
      {t('filters.noOptions', 'No {{filter}} to show', { filter: label })}
    </Typography>
  );
}
